import { useState, useEffect, useMemo } from "react";
import {
  Card, Metric, Text, Title, LineChart,
} from "@tremor/react";
import { PALETTE, CHART_PALETTE } from "../lib/palette";
import ThickDonut from "./ThickDonut";
import HeatmapUtilizacion, { UtilizacionData } from "./HeatmapUtilizacion";
import TendenciaUtilizacionCapacidad from "./TendenciaUtilizacionCapacidad";
import DashboardSidebar from "./DashboardSidebar";
import DashboardHeader, { RestaurantIdentity } from "./DashboardHeader";
import EstadoResultadosTable from "./EstadoResultadosTable";
import RevPASHBarras from "./RevPASHBarras";
import { useFilterContext } from "../context/FilterContext";
import { apiFetch } from "../api/client";

// ── Tipos (espejo del backend /api/financiero/*) ────────────

interface Kpi {
  valor: number | null;
  delta_pct: number | null;
  unidad: string;
  motivo: string | null;
}

interface ResumenResponse {
  restaurant_id: string;
  restaurant_name: string;
  anio: number;
  mm: number;
  mes_actual: string;
  ventas_totales: Kpi;
  margen_bruto: Kpi;
  margen_bruto_pct: Kpi;
  ebitda: Kpi;
  utilidad_operativa: Kpi;
  utilidad_neta: Kpi;
  food_cost: Kpi;
  beverage_cost: Kpi;
  prime_cost: Kpi;
  costo_laboral: Kpi;
  caidas_margen: Kpi;
}

interface TendenciaMensualRow {
  mes: number;
  label: string;
  ventas_2026: number | null;
  ventas_anterior: number | null;
  margen_pct_2026: number | null;
  margen_pct_anterior: number | null;
  ebitda_2026: number | null;
  ebitda_anterior: number | null;
}

interface TendenciaMensualResponse {
  restaurant_id: string;
  anio: number;
  anio_anterior: number;
  tiene_anio_anterior: boolean;
  rows: TendenciaMensualRow[];
}

// RevPASH (espejo de GET /api/financiero/revpash)
interface RevPASHRowLocal {
  mm: number;
  mes_label: string;
  ventas: number | null;
  sillas: number | null;
  horas: number | null;
  dias: number | null;
  revpash: number | null;
}
interface RevPASHResponse {
  restaurant_id: string;
  anio: number;
  rows: RevPASHRowLocal[];
}

// Ranking de productos por rentabilidad (espejo de GET /api/financiero/margen-producto/ranking)
interface ProductoRankingItem {
  producto: string;
  grupo: string;
  categoria: string;
  cantidad: number | null;
  venta_total: number | null;
  costo_total: number | null;
  utilidad_total: number | null;
  margen_pct: number | null;
}
interface RankingMargenResponse {
  restaurant_id: string;
  anio: number;
  mes: number;
  mes_actual: string;
  top: ProductoRankingItem[];
  bottom: ProductoRankingItem[];
}

interface PrimeCostMensual {
  mes: number;
  label: string;
  prime_cost_pct: number | null;
  food_cost_pct: number | null;
  labor_cost_pct: number | null;
}

interface PrimeCostResponse {
  restaurant_id: string;
  anio: number;
  mm: number;
  periodo_label: string;
  prime_cost_pct: number | null;
  food_cost_pct: number | null;
  labor_cost_pct: number | null;
  mensual: PrimeCostMensual[];
}

// ── Helpers ─────────────────────────────────────────────────

const formatCurrency = (n: number) =>
  new Intl.NumberFormat("es-PE", { style: "currency", currency: "PEN", maximumFractionDigits: 0 }).format(n);

const formatPct = (n: number) => `${n.toFixed(1)}%`;

function renderKpi(value: number | null | undefined, formatter: (n: number) => string): string {
  if (value == null) return "Sin datos";
  return formatter(value);
}

// Devuelve null cuando el valor es 0 o nulo, para que los gráficos de línea
// dejen un hueco en vez de dibujar una caída a cero (p.ej. meses de proyección
// de Cosme/Alado que existen en la BD con valor 0).
const toHueco = (v: number | null | undefined): number | null =>
  v == null || v === 0 ? null : v;

// Tarjeta de KPI reutilizable (evita duplicar markup)
function KpiCard({ label, value, formatter }: { label: string; value: number | null | undefined; formatter: (n: number) => string }) {
  return (
    <Card className="!p-3">
      <Text className="text-xs">{label}</Text>
      <Metric className="!text-xl">{renderKpi(value, formatter)}</Metric>
    </Card>
  );
}

// ── Componente principal ─────────────────────────────────────

export default function FinancieroDashboard() {
  const {
    restaurants, meses, selectedRestaurantId, selectedRestaurantSlug,
    mesSel, sidebarOpen, error, setSelectedRestaurant, setMesSel, toggleSidebar,
  } = useFilterContext();

  const [resumen, setResumen] = useState<ResumenResponse | null>(null);
  const [tendencia, setTendencia] = useState<TendenciaMensualResponse | null>(null);
  const [primeCost, setPrimeCost] = useState<PrimeCostResponse | null>(null);
  const [utilizacion, setUtilizacion] = useState<UtilizacionData | null>(null);
  const [revpash, setRevpash] = useState<RevPASHResponse | null>(null);
  const [rankingMargen, setRankingMargen] = useState<RankingMargenResponse | null>(null);

  const selectedRestaurant = useMemo(
    () => restaurants.find((r) => r.id === selectedRestaurantId) ?? null,
    [restaurants, selectedRestaurantId]
  );

  // ── Fetch: resumen (KPIs) ──
  useEffect(() => {
    if (!selectedRestaurantSlug || !mesSel) {
      setResumen(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams({
          restaurant_id: selectedRestaurantSlug,
          anio: String(mesSel.anio),
          mm: String(mesSel.mm),
        });
        const res = await apiFetch(`/api/financiero/resumen?${params}`);
        if (!res.ok) {
          if (!cancelled) setResumen(null);
          return;
        }
        const body: ResumenResponse = await res.json();
        if (!cancelled) setResumen(body);
      } catch (err) {
        console.warn("Error fetching resumen:", err);
        if (!cancelled) setResumen(null);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedRestaurantSlug, mesSel]);

  // ── Fetch: tendencias mensuales 2026 vs año anterior ──
  useEffect(() => {
    if (!selectedRestaurantSlug) {
      setTendencia(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams({
          restaurant_id: selectedRestaurantSlug,
          anio: "2026",
        });
        const res = await apiFetch(`/api/financiero/tendencias-mensuales?${params}`);
        if (!res.ok) {
          if (!cancelled) setTendencia(null);
          return;
        }
        const body: TendenciaMensualResponse = await res.json();
        if (!cancelled) setTendencia(body);
      } catch (err) {
        console.warn("Error fetching tendencias financieras:", err);
        if (!cancelled) setTendencia(null);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedRestaurantSlug]);

  // ── Fetch: análisis prime cost (composición + evolución mensual) ──
  useEffect(() => {
    if (!selectedRestaurantSlug) {
      setPrimeCost(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams({ restaurant_id: selectedRestaurantSlug });
        if (mesSel) {
          params.set("anio", String(mesSel.anio));
          params.set("mm", String(mesSel.mm));
        } else {
          params.set("anio", "2026");
        }
        const res = await apiFetch(`/api/financiero/prime-cost?${params}`);
        if (!res.ok) {
          if (!cancelled) setPrimeCost(null);
          return;
        }
        const body: PrimeCostResponse = await res.json();
        if (!cancelled) setPrimeCost(body);
      } catch (err) {
        console.warn("Error fetching prime cost:", err);
        if (!cancelled) setPrimeCost(null);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedRestaurantSlug, mesSel]);

  // ── Fetch: heatmap utilización de capacidad + RevPASH ──
  useEffect(() => {
    if (!selectedRestaurantSlug) {
      setUtilizacion(null);
      setRevpash(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const anio = mesSel ? String(mesSel.anio) : "2026";
        const params = new URLSearchParams({
          restaurant_id: selectedRestaurantSlug,
          anio,
          mm_ini: "1",
          mm_fin: String(mesSel ? mesSel.mm : 12),
        });
        const [resU, resR] = await Promise.all([
          apiFetch(`/api/financiero/utilizacion-capacidad?${params}`),
          apiFetch(`/api/financiero/revpash?restaurant_id=${encodeURIComponent(selectedRestaurantSlug)}&anio=${anio}&mm=${mesSel ? mesSel.mm : 12}`),
        ]);
        if (!cancelled) {
          setUtilizacion(resU.ok ? await resU.json() : null);
          setRevpash(resR.ok ? await resR.json() : null);
        }
      } catch (err) {
        console.warn("Error fetching utilizacion/revpash:", err);
        if (!cancelled) { setUtilizacion(null); setRevpash(null); }
      }
    })();
    return () => { cancelled = true; };
  }, [selectedRestaurantSlug, mesSel]);

  // ── Fetch: ranking de productos por rentabilidad (margen por producto) ──
  useEffect(() => {
    if (!selectedRestaurantSlug || !mesSel) {
      setRankingMargen(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams({
          restaurant_id: selectedRestaurantSlug,
          anio: String(mesSel.anio),
          mes: String(mesSel.mm),
        });
        const res = await apiFetch(`/api/financiero/margen-producto/ranking?${params}`);
        if (!res.ok) {
          if (!cancelled) setRankingMargen(null);
          return;
        }
        const body: RankingMargenResponse = await res.json();
        if (!cancelled) setRankingMargen(body);
      } catch (err) {
        console.warn("Error fetching ranking margen producto:", err);
        if (!cancelled) setRankingMargen(null);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedRestaurantSlug, mesSel]);

  const tendenciaRows = useMemo(() => tendencia?.rows ?? [], [tendencia]);
  const tieneComparativo = tendencia?.tiene_anio_anterior ?? false;
  const serieActual = tendencia ? String(tendencia.anio) : "2026";
  const serieAnterior = tendencia ? String(tendencia.anio_anterior) : "2025";

  // Datos para la dona de composición del Prime Cost
  const primeDonutData = useMemo(() => {
    if (!primeCost || primeCost.food_cost_pct == null || primeCost.labor_cost_pct == null) return null;
    return [
      { name: "Food Cost", value: Math.abs(primeCost.food_cost_pct) },
      { name: "Labor Cost", value: Math.abs(primeCost.labor_cost_pct) },
    ];
  }, [primeCost]);

  const primeSerieData = useMemo(
    () => (primeCost?.mensual ?? []).map((r) => ({
      mes: r.label,
      "Prime Cost": toHueco(r.prime_cost_pct != null ? Math.abs(r.prime_cost_pct) : null),
      "Food Cost": toHueco(r.food_cost_pct != null ? Math.abs(r.food_cost_pct) : null),
      "Labor Cost": toHueco(r.labor_cost_pct != null ? Math.abs(r.labor_cost_pct) : null),
    })),
    [primeCost]
  );

  return (
    <div className="flex gap-3">
      {/* ── Sidebar compartida ── */}
      <DashboardSidebar
        onRestaurantChange={setSelectedRestaurant}
        onMonthChange={setMesSel}
        sidebarOpen={sidebarOpen}
        onToggleSidebar={toggleSidebar}
        restaurants={restaurants as RestaurantIdentity[]}
        selectedRestaurantId={selectedRestaurantId}
        months={meses}
        selectedMonth={mesSel}
      />

      {/* ── Contenido principal ── */}
      <div className="flex-1 min-w-0 space-y-3">
        {selectedRestaurant ? (
          <DashboardHeader
            title="Resumen Financiero"
            description="Estado de Resultados (P&L) — KPIs y detalle por periodo"
            restaurant={selectedRestaurant as RestaurantIdentity}
            period={resumen?.mes_actual}
          />
        ) : (
          <Title>Resumen Financiero</Title>
        )}

        {error && (
          <Card className="!p-3">
            <Text className="text-red-600">Error cargando restaurants: {error.message}</Text>
          </Card>
        )}

        {/* KPIs calculables desde el P&L */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <KpiCard label="💰 Ventas netas" value={resumen?.ventas_totales.valor} formatter={formatCurrency} />
          <KpiCard label="💹 Utilidad bruta" value={resumen?.margen_bruto.valor} formatter={formatCurrency} />
          <KpiCard label="📈 Utilidad Operativa" value={resumen?.utilidad_operativa.valor} formatter={formatCurrency} />
          <KpiCard label="💹 EBITDA" value={resumen?.ebitda.valor} formatter={formatCurrency} />
          <KpiCard label="💰 Resultado del ejercicio" value={resumen?.utilidad_neta.valor} formatter={formatCurrency} />
        </div>

        {/* Estado de Resultados resumido: mes vs anterior + acumulado del periodo */}
        <EstadoResultadosTable restaurantSlug={selectedRestaurantSlug} mesSel={mesSel} />

        {/* Evolución mensual: mismo lenguaje visual de Comercial / Ventas diarias */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          <Card className="!p-3">
            <Title className="!text-base">Ventas netas por mes</Title>
            <Text className="!text-xs">Evolución mensual · S/</Text>
            <LineChart
              className="mt-3 h-56"
              data={tendenciaRows.map((r) => ({
                mes: r.label,
                [serieActual]: toHueco(r.ventas_2026),
                ...(tieneComparativo ? { [serieAnterior]: toHueco(r.ventas_anterior) } : {}),
              }))}
              index="mes"
              categories={tieneComparativo ? [serieActual, serieAnterior] : [serieActual]}
              colors={tieneComparativo ? [PALETTE.primaryDark, PALETTE.orange] : [PALETTE.primaryDark]}
              curveType="natural"
              valueFormatter={formatCurrency}
              yAxisWidth={70}
              showLegend={tieneComparativo}
              showAnimation={false}
              connectNulls={false}
            />
          </Card>

          <Card className="!p-3">
            <Title className="!text-base">Margen bruto % por mes</Title>
            <Text className="!text-xs">(Ventas netas − CMV) ÷ Ventas netas</Text>
            <LineChart
              className="mt-3 h-56"
              data={tendenciaRows.map((r) => ({
                mes: r.label,
                [serieActual]: toHueco(r.margen_pct_2026),
                ...(tieneComparativo ? { [serieAnterior]: toHueco(r.margen_pct_anterior) } : {}),
              }))}
              index="mes"
              categories={tieneComparativo ? [serieActual, serieAnterior] : [serieActual]}
              colors={tieneComparativo ? [PALETTE.primaryDark, PALETTE.orange] : [PALETTE.primaryDark]}
              curveType="natural"
              valueFormatter={formatPct}
              yAxisWidth={55}
              showLegend={tieneComparativo}
              showAnimation={false}
              connectNulls={false}
            />
          </Card>

          <Card className="!p-3">
            <Title className="!text-base">EBITDA operativo por mes</Title>
            <Text className="!text-xs">Evolución mensual · S/</Text>
            <LineChart
              className="mt-3 h-56"
              data={tendenciaRows.map((r) => ({
                mes: r.label,
                [serieActual]: toHueco(r.ebitda_2026),
                ...(tieneComparativo ? { [serieAnterior]: toHueco(r.ebitda_anterior) } : {}),
              }))}
              index="mes"
              categories={tieneComparativo ? [serieActual, serieAnterior] : [serieActual]}
              colors={tieneComparativo ? [PALETTE.primaryDark, PALETTE.orange] : [PALETTE.primaryDark]}
              curveType="natural"
              valueFormatter={formatCurrency}
              yAxisWidth={70}
              showLegend={tieneComparativo}
              showAnimation={false}
              connectNulls={false}
            />
          </Card>
        </div>

        {/* Análisis del Prime Cost: composición + evolución mensual */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <Card className="!p-3">
            <Title className="!text-base">Composición del Prime Cost</Title>
            <Text className="!text-xs">
              {primeCost && primeCost.mm !== 0
                ? `Food Cost + Labor Cost · % sobre ventas · ${primeCost.periodo_label}`
                : "Food Cost + Labor Cost · % sobre ventas (H1)"}
            </Text>
            {primeDonutData ? (
              <div className="grid grid-cols-2 gap-4 items-center mt-3">
                <div style={{ height: 170 }}>
                  <ThickDonut
                    data={primeDonutData}
                    colors={[PALETTE.primaryDark, PALETTE.orange]}
                    centerLabel={
                      primeCost && primeCost.prime_cost_pct != null
                        ? `${Math.abs(primeCost.prime_cost_pct).toFixed(1)}%`
                        : "—"
                    }
                    centerSub={primeCost && primeCost.mm !== 0 ? `PRIME COST · ${primeCost.periodo_label}` : "PRIME COST"}
                    height={170}
                    innerRadius={58}
                    outerRadius={100}
                  />
                </div>
                {/* Leyenda lateral con valores y % */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2">
                      <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: PALETTE.primaryDark }} />
                      Food Cost
                    </span>
                    <span className="font-medium tabular-nums">
                      {primeCost && primeCost.food_cost_pct != null ? `${Math.abs(primeCost.food_cost_pct).toFixed(1)}%` : "—"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2">
                      <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: PALETTE.orange }} />
                      Labor Cost
                    </span>
                    <span className="font-medium tabular-nums">
                      {primeCost && primeCost.labor_cost_pct != null ? `${Math.abs(primeCost.labor_cost_pct).toFixed(1)}%` : "—"}
                    </span>
                  </div>
                  <div className="border-t border-tremor-border pt-2 flex items-center justify-between text-sm font-semibold">
                    <span>PRIME COST</span>
                    <span className="tabular-nums">
                      {primeCost && primeCost.prime_cost_pct != null ? `${Math.abs(primeCost.prime_cost_pct).toFixed(1)}%` : "—"}
                    </span>
                  </div>
                  {(() => {
                    const f = primeCost && primeCost.food_cost_pct != null ? Math.abs(primeCost.food_cost_pct) : null;
                    const l = primeCost && primeCost.labor_cost_pct != null ? Math.abs(primeCost.labor_cost_pct) : null;
                    return f != null && l != null ? (
                      <Text className="!text-[10px] text-tremor-content">
                        {f.toFixed(0)}% + {l.toFixed(0)}% = {(f + l).toFixed(0)}%
                      </Text>
                    ) : (
                      <Text className="!text-[10px] text-tremor-content">Sin composición disponible.</Text>
                    );
                  })()}
                </div>
              </div>
            ) : (
              <Text className="!text-xs text-tremor-content mt-3">Sin datos de Prime Cost para este restaurante.</Text>
            )}
          </Card>

          <Card className="!p-3">
            <Title className="!text-base">Evolución mensual del Prime Cost</Title>
            <Text className="!text-xs">Prime Cost · Food Cost · Labor Cost · % de ventas</Text>
            <LineChart
              className="mt-3 h-56"
              data={primeSerieData}
              index="mes"
              categories={["Prime Cost", "Food Cost", "Labor Cost"]}
              colors={[PALETTE.primaryDark, CHART_PALETTE[4], PALETTE.orange]}
              curveType="natural"
              valueFormatter={(n: number) => `${n.toFixed(1)}%`}
              yAxisWidth={50}
              showLegend={true}
              showAnimation={false}
              connectNulls={false}
            />
          </Card>
        </div>

        {/* Tres gráficos en la misma fila: heatmap (40%), tendencia (30%), RevPASH (30%) */}
        <div className="grid grid-cols-1 lg:grid-cols-10 gap-3 items-stretch">
          {/* Heatmap utilización de capacidad instalada — 40% */}
          <div className="lg:col-span-4 h-full">
            {utilizacion ? (
              <HeatmapUtilizacion data={utilizacion} />
            ) : (
              <Card className="!p-3 h-full">
                <Title className="!text-base">Utilización de la capacidad instalada</Title>
                <Text className="!text-xs text-tremor-content">
                  Sin datos de ocupabilidad para este restaurante o periodo. Sube el reporte
                  desde «Admin → Ocupabilidad» para activar este mapa de calor.
                </Text>
              </Card>
            )}
          </div>

          {/* Tendencia de utilización (total por mes) — 30% */}
          <div className="lg:col-span-3 h-full">
            {utilizacion ? (
              <TendenciaUtilizacionCapacidad data={utilizacion} />
            ) : (
              <Card className="!p-3 h-full">
                <Title className="!text-base">Tendencia de utilización (total por mes)</Title>
                <Text className="!text-xs text-tremor-content">
                  Sin datos de ocupabilidad para graficar la tendencia.
                </Text>
              </Card>
            )}
          </div>

          {/* RevPASH — 30% */}
          <div className="lg:col-span-3 h-full">
            <RevPASHBarras data={revpash ?? { restaurant_id: "", anio: 2026, rows: [] }} />
          </div>
        </div>

        {/* Ranking de productos por rentabilidad (margen por producto) */}
        <Card className="!p-3">
          <Title className="!text-base">🏆 Top 5 productos más rentables</Title>
          <Text className="!text-xs">
            Por utilidad total (S/) · {rankingMargen ? rankingMargen.mes_actual : "—"}
          </Text>
          {rankingMargen && rankingMargen.top.length > 0 ? (
            <div className="mt-2 space-y-1">
              {rankingMargen.top.map((p, i) => (
                <div key={`top-${i}`} className="flex items-center justify-between text-sm border-b border-tremor-border pb-1">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{p.producto}</div>
                    <div className="text-[10px] text-tremor-content">{p.grupo} / {p.categoria}</div>
                  </div>
                  <div className="text-right tabular-nums">
                    <div className="font-semibold">
                      {p.utilidad_total != null ? formatCurrency(p.utilidad_total) : "—"}
                    </div>
                    <div className="text-[10px] text-tremor-content">
                      {p.margen_pct != null ? `${p.margen_pct.toFixed(1)}% margen` : ""}
                      {p.cantidad != null ? ` · ${p.cantidad} uds` : ""}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Text className="!text-xs text-tremor-content mt-2">
              Sin datos de margen por producto para este restaurante o mes. Súbelo desde
              <span className="font-mono"> Admin → Margen por producto</span>.
            </Text>
          )}
        </Card>

        {(!resumen || (resumen.ventas_totales.valor == null &&
          resumen.margen_bruto.valor == null && resumen.ebitda.valor == null)) && (
          <Card className="!p-3">
            <Text className="!text-xs text-tremor-content">
              Este restaurante aún no tiene un Estado de Resultados cargado. Súbelo desde
              <span className="font-mono"> Admin → Carga Estado de Resultados</span> para
              habilitar los KPIs financieros.
            </Text>
          </Card>
        )}

      </div>
    </div>
  );
}
